import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Heart, Star, Plus, Edit, Trash2, TrendingUp, TrendingDown, Users, Calendar, Download, Share2, MoreVertical, Filter, SortAsc, SortDesc, RotateCcw, Palette, Sparkles, Tag, AlertTriangle, X, Package, Wallet, ChevronDown, Check, ChevronRight } from 'lucide-react';
import ModelPreview from '../components/ModelPreview';
import ModelViewer from '../components/ModelViewer';
import { normalizePieces } from '../utils/pieceNormalizer';
import PieceCard from '../components/PieceCard';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import { useWallet } from '../contexts/WalletContext';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { calculateSaleFeeWithUSD, logSaleFeeTransaction } from '../utils/saleFeeUtils';
import { completePurchaseWithDelegate, checkSufficientBalance } from '../utils/marketplaceTransactions';
import WalletRequired from '../components/WalletRequired';
import WalletConnectionModal from '../components/WalletConnectionModal';
import { getAllCapsules, deletePiece, getCapsuleForPiece, getAllPiecesByCreator } from '../utils/supabaseDatabase';
import { getMarketplacePiecesByCreator } from '../utils/marketplaceQueries';
import { tradeEvents } from '../utils/tradeEvents';
import { createGroupedPieceCards, isGroupedCard, getAvailablePieceFromGroup } from '../utils/pieceGrouping';
import GroupedPiecesModal from '../components/GroupedPiecesModal';
import { createOffer } from '../utils/offersContract';
import RequestTradeModal from '../components/RequestTradeModal';
import { useWalletAction } from '../hooks/useWalletAction';

const Created = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { logSaleFeeCollected } = useActivity();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { withWalletCheck } = useWalletAction();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedType, setSelectedType] = useState('piece');
  const [allItems, setAllItems] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [capsuleModal, setCapsuleModal] = useState({ isOpen: false, item: null });
  const [walletModal, setWalletModal] = useState({ isOpen: false, message: '' });
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null); // Track capsule info for current piece
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingPieces, setIsLoadingPieces] = useState(false);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const dropdownRef = useRef(null);

  // Helper function to create a File object from base64 data
  const createFileFromDataUrl = (dataUrl, fileName, fileType) => {
    if (!dataUrl) return null;
    
    try {
      // Convert base64 to blob
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: mimeString });
      
      // Create File object
      const file = new File([blob], fileName, { type: fileType });
      
      return file;
    } catch (error) {
      console.error('Error creating file from data URL:', error);
      return null;
    }
  };

  // Function to delete an item
  const handleDeleteItem = (item) => {
    setDeleteModal({ isOpen: true, item });
  };

  // Function to confirm deletion
  const confirmDelete = async () => {
    const { item } = deleteModal;
    
    try {
      if (item.type === 'piece') {
        // Delete from Supabase
        console.log('🗑️ Deleting piece from Supabase:', item.id);
        await deletePiece(item.id);
        console.log('✅ Piece deleted from Supabase');
        
        // Also remove from localStorage for backward compatibility
        const pieces = JSON.parse(localStorage.getItem('pieces') || '[]');
        const updatedPieces = pieces.filter(piece => piece.id !== item.id);
        localStorage.setItem('pieces', JSON.stringify(updatedPieces));
      } else if (item.type === 'capsule') {
        // Capsules are handled differently - they're in drafts or Supabase capsules table
        // For now, remove from local state
        console.log('🗑️ Deleting capsule:', item.id);
      }

      // Update local state
      setAllItems(prev => prev.filter(prevItem => prevItem.id !== item.id));
      
      // Close modal
      setDeleteModal({ isOpen: false, item: null });
      
      console.log(`✅ Deleted ${item.type}: ${item.name}`);
    } catch (error) {
      console.error('❌ Error deleting item:', error);
      showNotification(`Error deleting item: ${error.message}. Please try again.`, 'error');
    }
  };

  // Function to cancel deletion
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, item: null });
  };

  // Load capsules from Supabase
  const loadCapsules = async () => {
    console.log('🚀 loadCapsules called');
    if (!publicKey) {
      console.log('⚠️ No wallet connected, skipping capsule loading');
      return;
    }

    try {
      console.log('📡 Fetching capsules from Supabase...');
      const result = await getAllCapsules();
      const capsulesData = result.capsules || [];
      console.log('Loaded capsules from Supabase:', capsulesData);
      console.log('Connected wallet:', publicKey.toString());
      
      // Filter capsules by creator wallet (only show user's capsules)
      const userCapsules = capsulesData.filter(capsule => {
        const creatorWallet = capsule.creator_wallet || capsule.creators?.wallet;
        console.log(`Capsule "${capsule.name}": creator=${creatorWallet}, match=${creatorWallet === publicKey.toString()}`);
        return creatorWallet === publicKey.toString();
      });
      
      console.log(`✅ Filtered ${userCapsules.length} user capsules from ${capsulesData.length} total`);
      
      // Only show capsules that belong to the user (no fallback)
      const capsulestoShow = userCapsules;
      console.log(`📦 Showing ${capsulestoShow.length} capsules`);
      
      // Transform capsules to match our data structure
      const transformedCapsules = capsulestoShow.map(capsule => ({
        id: capsule.id,
        name: capsule.name,
        description: capsule.description,
        type: 'capsule',
        status: capsule.status,
        createdDate: capsule.created_at,
        createdAt: capsule.created_at,
        dropDate: capsule.dropped_at || capsule.created_at, // Use dropped_at, fallback to created_at
        pieceCount: capsule.pieceCount || capsule.piece_count || 0,
        banner: capsule.banner || capsule.banner_url,
        avgPrice: capsule.avgPrice || '0',
        creator: capsule.creators,
        price: capsule.avgPrice || '0',
        category: 'Capsule',
        rarity: 'Common',
        views: 0,
        likes: 0,
        isDraft: capsule.status === 'draft'
      }));
      
      // Update allItems with capsules
      setAllItems(prev => {
        const nonCapsuleItems = prev.filter(item => item.type !== 'capsule');
        const newItems = [...nonCapsuleItems, ...transformedCapsules];
        console.log(`✅ Updated allItems: ${nonCapsuleItems.length} pieces + ${transformedCapsules.length} capsules = ${newItems.length} total`);
        return newItems;
      });
    } catch (error) {
      console.error('Error loading capsules:', error);
    }
  };

  // Load ALL pieces created by this wallet from Supabase (including drafts, private, etc.)
  const loadPiecesFromSupabase = async () => {
    if (!publicKey) {
      console.log('⚠️ No wallet connected, skipping piece loading');
      return;
    }

    try {
      setIsLoadingPieces(true);
      console.log('🔍 Loading ALL pieces from Supabase for wallet:', publicKey.toString());
      
      // Fetch ALL pieces created by this wallet (including drafts, public, private, etc.)
      const result = await getAllPiecesByCreator(publicKey.toString(), 0, 100);
      console.log('✅ Loaded ALL pieces from Supabase:', result);
      
      // Normalize pieces to ensure consistent field names
      const normalizedPieces = normalizePieces(result.pieces || []);
      
      // Transform to match the UI structure
      const transformedPieces = normalizedPieces.map(piece => {
        console.log('🔍 Transforming piece with creator info:', {
          pieceName: piece.name,
          creators: piece.creators,
          creator: piece.creator,
          creator_wallet: piece.creator_wallet
        });
        
        return {
          id: piece.id,
          type: 'piece',
          name: piece.name,
          description: piece.description,
          category: piece.category,
          color: piece.color,
          price: typeof piece.price === 'number' ? `${piece.price} SOL` : (piece.price || '0 SOL'),
          rarity: piece.rarity || 'Common',
          status: piece.listing_status || (piece.mint_address ? 'listed' : 'draft'),
          createdDate: piece.createdAt,
          thumbnailUrl: piece.thumbnailUrl || '/images/piece-placeholder.png',
          previewUrl: piece.previewUrl,
          modelUrl: piece.modelUrl,
          has3DModel: piece.has3DModel,
          quantity: piece.quantity || 1,
          sold: piece.sold || 0,
          views: piece.views || 0,
          likes: piece.likes || 0,
          downloads: piece.downloads || 0,
          tags: piece.tags || [],
          // Include creator information - now it comes from Supabase
          creators: piece.creators, // Keep the Supabase creators object
          creator: piece.creator,
          originalCreator: piece.originalCreator || piece.creator,
          creator_wallet: piece.creator_wallet,
          // Include all original data
          ...piece
        };
      });
      
      // DON'T apply grouping for the Created page - show all individual pieces
      // Users want to see each piece they created separately, including all editions
      console.log('📦 Showing all individual pieces without grouping for Created page');
      console.log(`📊 Total pieces created by this wallet: ${transformedPieces.length}`);
      
      // Update allItems - keep capsules, replace pieces
      setAllItems(prev => {
        const capsuleItems = prev.filter(item => item.type === 'capsule');
        const newItems = [...transformedPieces, ...capsuleItems];
        console.log(`✅ Updated allItems: ${transformedPieces.length} pieces + ${capsuleItems.length} capsules = ${newItems.length} total`);
        return newItems;
      });
      console.log('✅ Transformed all pieces created by wallet:', transformedPieces);
      console.log('📊 Piece status breakdown:', {
        total: transformedPieces.length,
        minted: transformedPieces.filter(p => p.mint_address || p.status === 'listed' || p.status === 'minted').length,
        draft: transformedPieces.filter(p => p.status === 'draft').length,
        statuses: transformedPieces.map(p => ({ name: p.name, status: p.status, mint_address: !!p.mint_address }))
      });
    } catch (error) {
      console.error('❌ Error loading public pieces from Supabase:', error);
    } finally {
      setIsLoadingPieces(false);
    }
  };


  // Load data from Supabase on component mount
  useEffect(() => {
    console.log('🔄 useEffect triggered - isConnected:', isConnected, 'publicKey:', publicKey?.toString());
    if (isConnected && publicKey) {
      console.log('✅ Loading pieces and capsules...');
      const loadData = async () => {
        await loadPiecesFromSupabase();
        await loadCapsules();
      };
      loadData();
    }
  }, [isConnected, publicKey]);

  // Listen for trade events and reload pieces when trades complete
  useEffect(() => {
    console.log('📢 Created: Setting up trade event listener');
    const unsubscribe = tradeEvents.subscribe(async (tradeData) => {
      console.log('📢 Created: Trade event received, reloading pieces...', tradeData);
      if (isConnected && publicKey) {
        await loadPiecesFromSupabase();
        await loadCapsules();
      }
    });

    return () => {
      console.log('📢 Created: Cleaning up trade event listener');
      unsubscribe();
    };
  }, [isConnected, publicKey]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Recently Created' },
    { id: 'name', name: 'Name A-Z' },
    { id: 'views', name: 'Most Views' },
    { id: 'likes', name: 'Most Likes' },
    { id: 'price-high', name: 'Price: High to Low' },
    { id: 'price-low', name: 'Price: Low to High' }
  ];

  // Handle dropdown
  const handleSortChange = (optionId) => {
    console.log('🔄 Sort changed to:', optionId);
    setSortBy(optionId);
    setIsDropdownOpen(false);
  };


  const typeFilters = [
    { id: 'piece', name: 'Pieces', icon: Tag },
    { id: 'capsule', name: 'Capsules', icon: Sparkles }
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'piece': return 'bg-gradient-to-r from-purple-500 to-purple-600 border border-purple-400';
      case 'capsule': return 'bg-gradient-to-r from-indigo-500 to-indigo-600 border border-indigo-400';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600 border border-gray-400';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'piece': return 'Piece';
      case 'capsule': return 'Capsule';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gradient-to-r from-amber-500 to-amber-600 border border-amber-400';
      case 'minted': return 'bg-gradient-to-r from-blue-500 to-blue-600 border border-blue-400';
      case 'listed': return 'bg-gradient-to-r from-green-500 to-green-600 border border-green-400';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600 border border-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'minted': return 'Minted';
      case 'listed': return 'Listed';
      default: return 'Unknown';
    }
  };

  const getCategoryColor = (category) => {
    return 'bg-purple-500'; // All categories now use the same purple color
  };

  // Get items to display based on selected type
  const getItemsToDisplay = () => {
    return allItems.length > 0 ? allItems : [];
  };

  const itemsToDisplay = getItemsToDisplay();

  // Filter items based on search and filters
  const filteredItems = itemsToDisplay.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.color && item.color.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = item.type === selectedType;
    
    return matchesSearch && matchesType;
  });

  // Sort items based on selected sort option
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        // Handle multiple date field names
        const dateA = new Date(a.created_at || a.createdAt || a.createdDate || 0);
        const dateB = new Date(b.created_at || b.createdAt || b.createdDate || 0);
        return dateB - dateA;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'views':
        return (b.views || 0) - (a.views || 0);
      case 'likes':
        return (b.likes || 0) - (a.likes || 0);
      case 'price-high':
        // Handle price as string or number, and capsule avgPrice
        const priceA = parseFloat(String(a.price || a.avgPrice || a.listing_price || '0').replace(' SOL', '').replace('SOL', ''));
        const priceB = parseFloat(String(b.price || b.avgPrice || b.listing_price || '0').replace(' SOL', '').replace('SOL', ''));
        return priceB - priceA;
      case 'price-low':
        // Handle price as string or number, and capsule avgPrice
        const priceA2 = parseFloat(String(a.price || a.avgPrice || a.listing_price || '0').replace(' SOL', '').replace('SOL', ''));
        const priceB2 = parseFloat(String(b.price || b.avgPrice || b.listing_price || '0').replace(' SOL', '').replace('SOL', ''));
        return priceA2 - priceB2;
      default:
        return 0;
    }
  });
  
  // Log sorted items for debugging
  console.log('📊 Sorted items:', {
    sortBy,
    count: sortedItems.length,
    first3: sortedItems.slice(0, 3).map(item => ({
      name: item.name,
      date: item.created_at || item.createdAt || item.createdDate,
      price: item.price || item.avgPrice,
      views: item.views,
      likes: item.likes
    }))
  });

  const openCapsuleModal = (item) => {
    // Navigate to capsule page to show how it looks to other users
    navigate(`/capsule/${item.id}`);
  };

  const closeCapsuleModal = () => {
    setCapsuleModal({ isOpen: false, item: null });
  };

  const openPieceDetailModal = (item) => {
    console.log('Opening piece detail modal for:', item);
    console.log('Item data:', item);
    console.log('Creator data check:', {
      creators: item?.creators,
      creator: item?.creator,
      originalCreator: item?.originalCreator,
      creator_wallet: item?.creator_wallet
    });
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedItem = item ? normalizePieces([item])[0] : item;
    setPieceDetailModal({ isOpen: true, item: normalizedItem });
    
    // Check if piece belongs to a capsule
    if (item?.id) {
      (async () => {
        try {
          let originalId = item.id;
          const itemIdString = String(item.id);
          
          if (itemIdString.startsWith('supabase_')) {
            originalId = itemIdString.replace('supabase_', '');
            if (!isNaN(originalId)) {
              originalId = parseInt(originalId, 10);
            }
          } else if (itemIdString.startsWith('local_')) {
            console.log('🔍 Local piece detected, skipping capsule check');
            setPieceCapsuleInfo(null);
            return;
          }
          
          console.log('🔍 Checking capsule for piece:', { originalId, transformedId: item.id });
          const capsuleInfo = await getCapsuleForPiece(originalId);
          console.log('🔍 Capsule info result:', capsuleInfo);
          
          setPieceCapsuleInfo(capsuleInfo);
        } catch (error) {
          console.error('❌ Error fetching capsule info:', error);
          setPieceCapsuleInfo(null);
        }
      })();
    } else {
      setPieceCapsuleInfo(null);
    }
    
    // Check if piece has 3D model - check multiple sources
    const has3DModel = !!(
      item?.modelUrl && (
        item.modelUrl.includes('.glb') || 
        item.modelUrl.includes('.gltf') ||
        item.modelUrl.includes('data:model/') ||
        item.modelUrl.includes('blob:')
      )
    );
    
    const hasPreviewUrl = !!(
      item?.previewUrl && (
        item.previewUrl.includes('.glb') || 
        item.previewUrl.includes('.gltf') ||
        item.previewUrl.includes('data:model/') ||
        item.previewUrl.includes('blob:')
      )
    );
    
    const hasModelFile = !!(
      item?.modelFile?.dataUrl && (
        item.modelFile.dataUrl.includes('.glb') || 
        item.modelFile.dataUrl.includes('.gltf') ||
        item.modelFile.type === 'model/gltf-binary' ||
        item.modelFile.type === 'model/gltf+json'
      )
    );
    
    const hasAny3DModel = has3DModel || hasPreviewUrl || hasModelFile;
    
    console.log('3D Model check:', {
      has3DModel,
      hasPreviewUrl,
      hasModelFile,
      hasAny3DModel,
      itemPreviewUrl: item?.previewUrl,
      itemModelUrl: item?.modelUrl,
      itemModelFile: item?.modelFile?.dataUrl
    });
    
    if (hasAny3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
      console.log('3D model detected, setting view mode to 3d');
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
      console.log('No 3D model detected, setting view mode to thumbnail');
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setActivityPanelOpen(false); // Reset activity panel when closing modal
    setPieceCapsuleInfo(null);
  };

  // Handle view entire capsule button click
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      closePieceDetailModal();
    }
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.creators?.wallet || item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    } else {
      console.warn('No creator wallet found for item:', item);
    }
  };

  // Handle holder button click - navigate to holder profile
  const handleHolderClick = (item) => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };

  // Handle buy now button
  const handleBuyNow = (item) => {
    if (!isConnected) {
      setWalletModal({ isOpen: true, message: 'You need to connect your wallet to make a purchase.' });
      return;
    }
    setBuyModal({ isOpen: true, item });
  };

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      setWalletModal({ isOpen: true, message: 'Wallet not connected. Please connect your wallet to continue.' });
      return;
    }

    const { item } = buyModal;
    const price = parseFloat(item.price);
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      showNotification(`Insufficient balance. You need ${balanceCheck.required.toFixed(4)} SOL (including gas) but only have ${balanceCheck.balance.toFixed(4)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      
      const result = await completePurchaseWithDelegate(
        { connected: isConnected, publicKey, sendTransaction: window.solana?.signAndSendTransaction || window.solflare?.signAndSendTransaction },
        item,
        price
      );
      
      // Log sale fee activity
      logSaleFeeCollected({
        pieceId: item.id,
        pieceName: item.name,
        feeAmount: result.feeBreakdown.feeAmount,
        piecePrice: price,
        buyerWallet: publicKey.toString(),
        sellerWallet: item.holder_wallet || item.creators?.wallet || 'Unknown Seller',
        transactionHash: result.paymentTransaction.signature
      });
      
      // Show success message with transaction details
      const nftInfo = result.nftTransaction ? 
        `\n\n🎨 NFT Transferred: ${result.nftTransaction.signature.slice(0, 8)}...` : 
        '\n\nℹ️ Piece not minted on-chain yet';
      
      // Show success notification
      showNotification(
        `Successfully purchased ${item.name} for ${result.feeBreakdown.salePriceFormatted} SOL! 🎉`,
        'success'
      );
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // In a real app, you would:
      // 1. Create a transaction
      // 2. Send SOL to the seller (minus fee)
      // 3. Send fee to platform revenue wallet
      // 4. Transfer the NFT to the buyer
      // 5. Update the database
      
    } catch (error) {
      console.error('Purchase failed:', error);
      showNotification('Purchase failed. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck(async (item) => {
    // Check if user already has an active offer on this piece
    try {
      const { getPieceOffers } = await import('../utils/offersContract');
      const offersResult = await getPieceOffers(item.mint_address, 'active');
      
      if (offersResult.success && offersResult.offers.length > 0) {
        // Check if any of the active offers are from this user
        const userOffer = offersResult.offers.find(
          offer => offer.offerer_wallet === publicKey?.toString()
        );
        
        if (userOffer) {
          showNotification(
            `You already have an active offer of ${userOffer.offer_price} SOL on this piece. Visit the Offers page to view or update it.`,
            'info'
          );
          return;
        }
      }
    } catch (error) {
      console.log('Could not check for existing offers:', error);
      // Continue anyway - the backend will catch duplicates
    }
    
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7 });
  }, 'make an offer');

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (!offerPrice || offerPrice <= 0 || isNaN(offerPrice)) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays || 7,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      
      // Show success notification
      showNotification(`Offer of ${offerPrice} SOL submitted successfully! 🎉`, 'success');
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  };

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request submission
  const handleTradeRequestSubmit = (tradeRequest) => {
    console.log('Trade request submitted:', tradeRequest);
    // Close both the trade modal and the piece detail modal
    setRequestTradeModal({ isOpen: false, item: null });
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    showNotification(message, 'success');
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to view and manage your created pieces."
      action="manage your pieces"
    >
      <div className="min-h-screen bg-black">
        {/* Delete Confirmation Modal */}
        {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Delete Item</h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete <span className="text-white font-medium">"{deleteModal.item?.name}"</span>? 
              This will permanently remove it from your creations.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - 3D Model/Thumbnail */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  // Also check for modelFile with 3D model data
                  const hasModelFile = pieceDetailModal.item?.modelFile?.dataUrl && (
                    pieceDetailModal.item.modelFile.dataUrl.includes('.glb') || 
                    pieceDetailModal.item.modelFile.dataUrl.includes('.gltf') ||
                    pieceDetailModal.item.modelFile.type === 'model/gltf-binary' ||
                    pieceDetailModal.item.modelFile.type === 'model/gltf+json'
                  );
                  
                  if (has3DModel || hasModelFile) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square">
                  {(() => {
                    if (modalViewMode === '3d') {
                      // Show 3D model
                      const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                      const modelBucket = normalizedPiece?.previewBucket || normalizedPiece?.modelBucket;
                      const modelKey = normalizedPiece?.previewKey || normalizedPiece?.modelKey;
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      // Check for modelFile data
                      const modelFileSrc = pieceDetailModal.item?.modelFile?.dataUrl && (
                        pieceDetailModal.item.modelFile.dataUrl.includes('.glb') || 
                        pieceDetailModal.item.modelFile.dataUrl.includes('.gltf') ||
                        pieceDetailModal.item.modelFile.type === 'model/gltf-binary' ||
                        pieceDetailModal.item.modelFile.type === 'model/gltf+json'
                      ) ? pieceDetailModal.item.modelFile.dataUrl : null;
                      
                      if (modelBucket && modelKey) {
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc || modelFileSrc) {
                        const srcToUse = modelFileSrc || fallbackSrc;
                        return (
                          <ModelViewer
                            src={srcToUse}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : pieceDetailModal.item?.modelFile?.dataUrl && pieceDetailModal.item.modelFile.dataUrl.startsWith('data:image/') ? (
                        <img 
                          src={pieceDetailModal.item.modelFile.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(() => {
                          const creatorData = pieceDetailModal.item?.creators || pieceDetailModal.item?.creator || pieceDetailModal.item?.originalCreator;
                          const profilePic = creatorData?.profile_picture_url || creatorData?.profilePicture;
                          const creatorName = creatorData?.handle || creatorData?.username || pieceDetailModal.item?.creator_wallet?.slice(0, 8) || 'Creator';
                          
                          return profilePic ? (
                            <img
                              src={profilePic}
                              alt={creatorName}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {creatorName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {(() => {
                              const creatorData = pieceDetailModal.item?.creators || pieceDetailModal.item?.creator || pieceDetailModal.item?.originalCreator;
                              return creatorData?.handle || creatorData?.username || pieceDetailModal.item?.creator_wallet?.slice(0, 8) + '...' || 'Original Creator';
                            })()}
                          </div>
                          {(() => {
                            const creatorData = pieceDetailModal.item?.creators || pieceDetailModal.item?.creator || pieceDetailModal.item?.originalCreator;
                            return (creatorData?.is_verified || creatorData?.verified) && (
                              <img 
                                src="/images/Verification badge.png" 
                                alt="Verified" 
                                className="w-4 h-4 object-contain flex-shrink-0"
                              />
                            );
                          })()}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {(() => {
                            const creatorData = pieceDetailModal.item?.creators || pieceDetailModal.item?.creator || pieceDetailModal.item?.originalCreator;
                            const piecesCount = creatorData?.total_pieces_created || creatorData?.piecesCount || 0;
                            return `${piecesCount} pieces created`;
                          })()}
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (pieceDetailModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // DEBUG: Log social links data
                      console.log('🔍 [CREATED] Social Links Debug:', {
                        pieceId: pieceDetailModal.item?.id,
                        pieceName: pieceDetailModal.item?.name,
                        pieceCapsuleInfo: pieceCapsuleInfo,
                        usingCapsuleLinks: !!pieceCapsuleInfo,
                        social_links: socialLinks,
                        hasSocialLinks
                      });
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                          {socialLinks.website && (
                            <a
                              href={socialLinks.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Website"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="X (Twitter)"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.instagram && (
                            <a
                              href={socialLinks.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Instagram"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.discord && (
                            <a
                              href={socialLinks.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Discord"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title="Add to favorites"
                      >
                        <Heart 
                          size={20} 
                          className="text-gray-400 hover:text-red-500 transition-all duration-300" 
                        />
                      </button>
                    </div>
                    {/* Custom Tags and Activity Button */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                          pieceDetailModal.item.tags.map((tag, index) => (
                            <span 
                              key={index}
                              className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No tags</span>
                        )}
                      </div>
                      <button
                        onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                          activityPanelOpen 
                            ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                            : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                        }`}
                      >
                        Activity
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {pieceDetailModal.item?.category || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const isOwnedByCurrentUser = publicKey && item?.holder_wallet === publicKey.toString();
                            const isListed = item?.listing_status === 'listed';
                            
                            let displayPrice;
                            // If user owns this piece
                            if (isOwnedByCurrentUser) {
                              // If they've listed it, show THEIR listing price
                              if (isListed && item?.listing_price) {
                                displayPrice = item.listing_price;
                              }
                              // If not listed, show purchase price
                              else if (!isListed && item?.last_sale_price_lamports) {
                                displayPrice = item.last_sale_price_lamports / 1_000_000_000;
                              }
                              else {
                                displayPrice = item?.price || 0;
                              }
                            } else {
                              // For pieces not owned by user, show lowest available price
                              displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            }
                            
                            // Format price to remove trailing zeros
                            const formatted = typeof displayPrice === 'number' 
                              ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                              : displayPrice;
                            return formatted;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const isOwnedByCurrentUser = publicKey && item?.holder_wallet === publicKey.toString();
                          const isListed = item?.listing_status === 'listed';
                          
                          let displayPrice;
                          if (isOwnedByCurrentUser) {
                            if (isListed && item?.listing_price) {
                              displayPrice = item.listing_price;
                            } else if (!isListed && item?.last_sale_price_lamports) {
                              displayPrice = item.last_sale_price_lamports / 1_000_000_000;
                            } else {
                              displayPrice = item?.price || 0;
                            }
                          } else {
                            displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          }
                          return ((displayPrice || 0) * 195.84).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    {pieceDetailModal.item?.status === 'draft' ? 'Created' : 'Dropped'} {pieceDetailModal.item?.createdDate ? new Date(pieceDetailModal.item.createdDate).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {/* Minted/Active NFT actions - only share since you created it */}
                    <button 
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: pieceDetailModal.item?.name,
                            text: `Check out this digital fashion piece: ${pieceDetailModal.item?.name}`,
                            url: window.location.href
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          showNotification('Link copied to clipboard!', 'success');
                        }
                      }}
                      className="w-full px-4 py-3.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg"
                    >
                      🔗 Share
                    </button>

                    {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                    {pieceCapsuleInfo && (
                      <div className="pt-3">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCapsule();
                          }}
                          className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <Package size={14} />
                          View entire capsule
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {(() => {
                    const price = pieceDetailModal.item?.price;
                    const basePrice = typeof price === 'string' 
                      ? parseFloat(price.replace(' SOL', '').replace('SOL', '')) || 0
                      : parseFloat(price) || 0;
                    const totalQty = pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1;
                    
                    console.log('📊 [Created] PieceActivityAndHolders props:', {
                      pieceId: pieceDetailModal.item?.id,
                      pieceName: pieceDetailModal.item?.name,
                      basePrice,
                      solPrice: solPrice || 150,
                      totalQuantity: totalQty
                    });
                    
                    return (
                      <PieceActivityAndHolders
                        pieceId={pieceDetailModal.item?.id}
                        pieceName={pieceDetailModal.item?.name}
                        basePrice={basePrice}
                        solPrice={solPrice || 150}
                        totalQuantity={totalQty}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grouped Pieces Modal */}
      {groupedPiecesModal.isOpen && (
        <GroupedPiecesModal
          isOpen={groupedPiecesModal.isOpen}
          onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
          groupedCard={groupedPiecesModal.groupedCard}
          onBuyNow={(selectedPiece) => {
            handleBuyNow(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
          onMakeOffer={(selectedPiece) => {
            handleMakeOffer(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
          onRequestTrade={(selectedPiece) => {
            handleRequestTrade(selectedPiece);
            setGroupedPiecesModal({ isOpen: false, groupedCard: null });
          }}
          solPrice={solPrice || 195.84}
          wallet={wallet}
          publicKey={publicKey}
          isConnected={isConnected}
        />
      )}

      {/* Capsule Detail Modal */}
      {capsuleModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeCapsuleModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-[#2a2a2a]">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-3xl font-bold text-white">
                      {capsuleModal.item?.name}
                    </h2>
                    <span className="bg-gradient-to-r from-indigo-500 to-indigo-600 border border-indigo-400 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      Capsule
                    </span>
                    <span className={`${getStatusColor(capsuleModal.item?.status)} text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg`}>
                      {getStatusText(capsuleModal.item?.status)}
                    </span>
                  </div>
                  <p className="text-gray-400 text-lg">
                    {capsuleModal.item?.description || `A curated collection of ${capsuleModal.item?.pieceCount || 0} digital fashion pieces with modern styling and premium quality.`}
                  </p>
                </div>
                <button
                  onClick={closeCapsuleModal}
                  className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
                >
                  <X size={24} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-200px)]">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-white">{capsuleModal.item?.pieceCount || 0}</div>
                  <div className="text-sm text-gray-400">Pieces</div>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-white">{capsuleModal.item?.views || 0}</div>
                  <div className="text-sm text-gray-400">Views</div>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-white">{capsuleModal.item?.likes || 0}</div>
                  <div className="text-sm text-gray-400">Likes</div>
                </div>
              </div>

              {/* Pieces Grid */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-4">Pieces in this Capsule</h3>
                {capsuleModal.item?.collectionModels && capsuleModal.item.collectionModels.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                    {capsuleModal.item.collectionModels.map((piece, index) => (
                      <div 
                        key={index}
                        className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20"
                      >
                        {/* Thumbnail/3D Model Preview Area */}
                        <div className="aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center relative group-hover:bg-[#3a3a3a] transition-colors duration-200 overflow-hidden">
                          {piece.thumbnail?.dataUrl ? (
                            <img 
                              src={piece.thumbnail.dataUrl} 
                              alt={piece.name || `Piece ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : piece.dataUrl ? (
                            <div className="w-full h-full">
                              <ModelPreview
                                file={createFileFromDataUrl(
                                  piece.dataUrl,
                                  piece.name || `Piece ${index + 1}`,
                                  piece.type || 'model/gltf-binary'
                                )}
                                width="100%"
                                height="100%"
                              />
                            </div>
                          ) : (
                            <img 
                              src="/images/piece-placeholder.png" 
                              alt="No preview available"
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>

                        {/* Piece Info */}
                        <div className="p-3 space-y-2">
                          <h4 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                            {piece.name || `Piece ${index + 1}`}
                          </h4>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">#{index + 1}</span>
                            <span className="text-xs text-indigo-400 font-medium">In Capsule</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <div className="text-4xl">📦</div>
                    </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">No pieces in this capsule</h3>
                    <p className="text-gray-500">This capsule doesn't contain any pieces yet.</p>
                  </div>
                )}
              </div>

              {/* Pricing Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6">
                <h3 className="text-white font-semibold mb-3">Pricing Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Average Price</div>
                    <div className="text-green-400 font-bold text-xl">
                      {capsuleModal.item?.avgPrice || capsuleModal.item?.price || '0.0'} SOL
                    </div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${((parseFloat(capsuleModal.item?.avgPrice || capsuleModal.item?.price || '0') * 195.84)).toFixed(2)} USD
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Total Value</div>
                    <div className="text-white font-bold text-xl">
                      {((parseFloat(capsuleModal.item?.avgPrice || capsuleModal.item?.price || '0') * (capsuleModal.item?.pieceCount || 1))).toFixed(2)} SOL
                    </div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${((parseFloat(capsuleModal.item?.avgPrice || capsuleModal.item?.price || '0') * (capsuleModal.item?.pieceCount || 1)) * 195.84).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-[#2a2a2a]">
              <div className="flex gap-3">
                {capsuleModal.item?.status === 'draft' ? (
                  <>
                    <button 
                      onClick={() => {
                        closeCapsuleModal();
                        navigate('/create-collection');
                      }}
                      className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors font-medium"
                    >
                      Edit Capsule
                    </button>
                    <button 
                      onClick={() => {
                        closeCapsuleModal();
                        handleDeleteItem(capsuleModal.item);
                      }}
                      className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium"
                    >
                      Delete Capsule
                    </button>
                    <button 
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: capsuleModal.item?.name,
                            text: `Check out this digital fashion capsule: ${capsuleModal.item?.name}`,
                            url: window.location.href
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          showNotification('Link copied to clipboard!', 'success');
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors font-medium"
                    >
                      Share
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: capsuleModal.item?.name,
                            text: `Check out this digital fashion capsule: ${capsuleModal.item?.name}`,
                            url: window.location.href
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          showNotification('Link copied to clipboard!', 'success');
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors font-medium"
                    >
                      Share
                    </button>
                    <div className="flex-1 px-4 py-3 bg-gray-800 text-gray-500 rounded-xl text-center flex items-center justify-center">
                      <span className="text-sm">Immutable Capsule</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{buyModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Price</span>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-lg">{buyModal.item?.price}</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(buyModal.item?.price || '0') * 195.84).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * 195.84).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0')}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Tag size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                {offerModal.item?.listing_price && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Current Price</span>
                    <span className="text-gray-300">{offerModal.item.listing_price} SOL</span>
                  </div>
                )}
              </div>

              {/* Offer Amount */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-2">Your Offer (SOL)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter amount in SOL"
                  value={offerModal.offerAmount}
                  onChange={(e) => setOfferModal({ ...offerModal, offerAmount: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="text-gray-400 text-sm mt-2">
                    ≈ ${(parseFloat(offerModal.offerAmount) * (solPrice || 195.84)).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Expiration */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-2">Expires in (days)</label>
                <select
                  value={offerModal.expirationDays}
                  onChange={(e) => setOfferModal({ ...offerModal, expirationDays: parseInt(e.target.value) })}
                  className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>

              {/* Wallet Balance */}
              {isConnected && (
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Your Balance</span>
                    <span className="text-white font-medium">{balance.toFixed(4)} SOL</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-xl transition-all duration-200 font-medium border border-[#2a2a2a] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !offerModal.offerAmount || parseFloat(offerModal.offerAmount) <= 0}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Submitting...' : 'Submit Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={handleTradeRequestSubmit}
        onSuccess={handleTradeRequestSuccess}
      />

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className={`rounded-xl p-4 shadow-2xl border ${
            notification.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : notification.type === 'error'
              ? 'bg-red-500/10 border-red-500/30 text-red-400'
              : 'bg-blue-500/10 border-blue-500/30 text-blue-400'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                notification.type === 'success'
                  ? 'bg-green-500'
                  : notification.type === 'error'
                  ? 'bg-red-500'
                  : 'bg-blue-500'
              }`}></div>
              <p className="font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="max-w-full pl-4 pr-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">My Creations</h1>
          <p className="text-gray-400">Manage and showcase your created pieces and capsule collections</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="pl-4 pr-4 py-6 border-b border-[#2a2a2a] bg-black">
        <div className="max-w-7xl">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="w-80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search your creations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-[#2a2a2a] rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                  style={{ backgroundColor: '#0a0a0a' }}
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-300">Type:</span>
                <div className="flex gap-2">
                  {typeFilters.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedType === type.id
                          ? 'bg-[#2a2a2a] text-white'
                          : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a] hover:text-white'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Sort by:</span>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === sortBy
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === sortBy && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-full pl-4 pr-4 py-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Creation Portfolio</h2>
              <p className="text-gray-400 text-sm">
                {itemsToDisplay.length === 0 ? 'Start creating your digital fashion' : `${itemsToDisplay.length} item${itemsToDisplay.length !== 1 ? 's' : ''} created`}
              </p>
            </div>
            <button 
              onClick={() => navigate('/create-nft')}
              className="flex items-center gap-2 px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium shadow-lg"
            >
              <Plus size={20} />
              Create New
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mb-6 p-6 bg-[#0a0a0a] rounded-xl border border-[#2a2a2a]">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{itemsToDisplay.length}</div>
              <div className="text-sm text-gray-400">Total Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {itemsToDisplay.filter(item => 
                  item.mint_address || 
                  item.status === 'listed' || 
                  item.status === 'minted' ||
                  (item.listing_status && item.listing_status !== 'draft')
                ).length}
              </div>
              <div className="text-sm text-gray-400">Minted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {itemsToDisplay.filter(item => 
                  item.status === 'listed' || item.listing_status === 'listed'
                ).length}
              </div>
              <div className="text-sm text-gray-400">Listed</div>
            </div>
          </div>
        </div>

        {/* Items Display */}
        {isLoadingPieces ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Palette size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-400 mb-2">Loading your creations...</h3>
            <p className="text-gray-500">Please wait while we fetch your pieces from Supabase</p>
          </div>
        ) : sortedItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Palette size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-400 mb-2">
              {itemsToDisplay.length === 0 ? 'No creations yet' : 'No items found'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {itemsToDisplay.length === 0 
                ? 'Start building your digital fashion portfolio by creating your first piece or capsule'
                : 'Try adjusting your search criteria or filters'
              }
            </p>
            {itemsToDisplay.length === 0 && (
              <button 
                onClick={() => navigate('/create-nft')}
                className="flex items-center gap-2 px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium  shadow-lg mx-auto"
              >
                <Plus size={20} />
                Create Your First Item
              </button>
            )}
          </div>
        ) : (
          <div className={`grid gap-6 ${selectedType === 'capsule' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8'}`}>
            {sortedItems.map((item) => {
              // Render capsules with wrapper div, pieces without wrapper
              if (item.type === 'capsule') {
                return (
                  <div 
                    key={item.id}
                    className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
                  >
                    <div 
                      onClick={() => openCapsuleModal(item)}
                      className="cursor-pointer group relative h-full flex flex-col"
                    >
                      {/* Banner Image - Top half */}
                      <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                        {item.banner ? (
                          <img 
                            src={item.banner} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-4xl opacity-30">📦</div>
                        )}

                        {/* Heart Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCapsuleFavorite(item);
                          }}
                          className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        >
                          <Heart 
                            size={16} 
                            className={`${
                              isCapsuleFavorited(item.id) 
                                ? 'text-red-500 fill-red-500' 
                                : 'text-white/70 hover:text-red-500'
                            } transition-all duration-300`} 
                          />
                        </button>
                      </div>

                      {/* Bottom Section with Info - Bottom half */}
                      <div className="h-1/2 p-3 flex flex-col justify-between">
                        <div className="space-y-1">
                          {/* Title */}
                          <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                            {item.name}
                          </h3>

                          {/* Description */}
                          <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                            {item.description || (() => {
                              const count = item.pieceCount || 0;
                              return `A curated collection of ${count} digital fashion ${count === 1 ? 'piece' : 'pieces'} with modern styling and premium quality.`;
                            })()}
                          </p>

                          {/* Pieces Count */}
                          <div className="text-gray-400 text-xs">
                            • {item.pieceCount || item.piece_count || 0} {(item.pieceCount || item.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                          </div>
                        </div>

                        <div className="space-y-1">
                          {/* Avg Price Row */}
                          {(item.price || item.avgPrice) && (
                            <div className="flex items-center justify-between">
                              <div className="text-gray-400 text-xs">Avg Price</div>
                              <div className="text-right">
                                <div className="text-green-400 font-semibold text-sm">
                                  {item.price || item.avgPrice} SOL
                                </div>
                                <div className="text-gray-500 text-xs">
                                  ~${((parseFloat(item.price || item.avgPrice || 0)) * (solPrice || 195.84)).toFixed(2)} USD
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Drop Time */}
                          {(item.created_at || item.createdAt || item.createdDate || item.dropDate) && (
                            <div className="text-gray-500 text-xs">
                              Dropped {new Date(item.created_at || item.createdAt || item.createdDate || item.dropDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                          )}

                          {/* Delete Button for Drafts Only */}
                          {item.status === 'draft' && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteItem(item);
                              }}
                              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border border-red-400 text-white text-xs font-semibold py-2.5 px-3 rounded-full shadow-lg transition-all duration-200"
                            >
                              Delete Draft
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Render pieces directly without wrapper - UnifiedPieceCard has its own styling
                return (
                  <UnifiedPieceCard
                    key={item.id}
                    item={{
                      ...item,
                      price: typeof item.price === 'string' ? item.price.replace(' SOL', '') : item.price, // Remove ' SOL' suffix if present and price is string
                      // thumbnailUrl is already set in the data transformation above
                    }}
                    onClick={openPieceDetailModal}
                    onBuy={undefined} // Remove buy button for own pieces
                    onTrade={undefined} // Remove trade button for own pieces
                    onFavorite={() => console.log('Toggle favorite for item:', item.name)}
                    solPrice={195.84}
                    showBuyButton={false} // Hide buy button for own pieces
                    showTradeButton={false} // Hide trade button for own pieces
                    showFavoriteButton={false}
                    buyButtonText="Buy now"
                    tradeButtonText="Trade"
                  />
                );
              }
            })}
          </div>
        )}
      </div>

      {/* Wallet Connection Modal */}
      <WalletConnectionModal
        isOpen={walletModal.isOpen}
        onClose={() => setWalletModal({ isOpen: false, message: '' })}
        message={walletModal.message}
      />
    </div>
    </WalletRequired>
  );
};

export default Created; 